import React, {FunctionComponent, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Dialog, Intent} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {loadUsers} from '../../../store/actions/userActions';
import {IState} from '../../../store/reducers';
import {TRIGGER_SOURCE, TRIGGER_TYPE} from './constants';
import {SelectTriggerType} from './components';
import ScheduleForm from './ScheduleForm';
import WebhookForm from './WebhookForm';

import './TriggerDialog.scss';

interface TriggerDialogProps {
  show: boolean;
  source: TRIGGER_SOURCE;
  onClose?: () => void;
  data?: any | null; // edit data
  type?: string | null;
  showTriggerType?: boolean;
  showDelete?: boolean;
}

const Footer = ({
  onClose,
  onSave,
  onDelete,
  edit= false,
  showDelete = true,
}: any) => {
  /*
   * We don't use DialogFooter and ButtonGroup because their CSS messes with SelectBox
   *   and content from the SelectBox is rendered under the DialogFooter buttons.
   */
  const actionButtonText = edit ? 'Update' : 'Create';

  return (
    <div className="trigger-dialog__footer">
      <div className="left-side">
        {
          showDelete && edit && <Button
            icon={IconNames.TRASH}
            text="Delete"
            intent={Intent.DANGER}
            onClick={onDelete}
            minimal={true}
          />
        }
      </div>
      <div className="right-side">
        <Button text="Cancel" onClick={onClose}/>
        <Button text={actionButtonText} intent={Intent.PRIMARY} onClick={onSave} />
      </div>
    </div>
  )
};


const TriggerDialog: FunctionComponent<TriggerDialogProps> = ({
  show,
  source,
  onClose,
  type,
  data,
  showTriggerType = true,
  showDelete = true,
}: TriggerDialogProps) => {
  /*
   * Based on `notebook` or `report` and `TRIGGER_SOURCE`, we determine which notebook/report is being edited.
   */
  const item = useSelector((state: IState) => state.reportForm.report); // current report
  const uid = item?.uid;
  const id = item?.id;
  const hasWebhook = item ? !!item?.webhook : false;

  const editMode = !!data;
  const title = editMode ? 'Update Execution Trigger' : 'New Execution Trigger';
  const [triggerType, setTriggerType] = useState<string>(type || TRIGGER_TYPE.WORKFLOW);
  const dispatch = useDispatch();
  const formRef = useRef<any>(null);

  const isWorkflow = triggerType === TRIGGER_TYPE.WORKFLOW; // schedule
  const isWebhook = triggerType === TRIGGER_TYPE.WEBHOOK;

  useEffect(() => {
    const loadAllUsers = {page: 0, page_size: -1, reload: true};
    // used for selecting recipients
    dispatch(loadUsers(loadAllUsers));
  }, []);

  const onSave = () => {
    onClose && onClose();
    formRef.current.onSave();
    setTriggerType(TRIGGER_TYPE.WORKFLOW);
  };

  const onDelete = () => {
    onClose && onClose();
    formRef.current.onDelete();
    setTriggerType(TRIGGER_TYPE.WORKFLOW);
  }

  return (
    <Dialog
      title={title}
      isOpen={show}
      icon={IconNames.LIGHTNING}
      className="trigger-dialog"
      onClose={onClose}
    >
      {
        showTriggerType && !editMode &&
        <SelectTriggerType
          type={triggerType}
          onChange={setTriggerType}
          hasWebhook={hasWebhook}
        />
      }
      {isWorkflow &&
        <ScheduleForm
          ref={formRef}
          source={source}
          data={data}
          edit={editMode}
          sourceId={id as number}
        />
      }
      {isWebhook &&
        <WebhookForm
          ref={formRef}
          source={source}
          data={data}
          edit={editMode}
          id={id as number}
          uid={uid as string}
        />
      }
      <Footer
        onClose={onClose}
        onSave={onSave}
        onDelete={onDelete}
        edit={editMode}
        showDelete={showDelete}
      />
    </Dialog>
  );
};

export default TriggerDialog;
